import { createContext, useContext } from 'react';
import { AI_BUILDER_MENTION_ID, AI_BUILDER_MENTION_TYPE, aiBuilderMentionId } from '@shared/task-contexts';
import { TASK_AI_BUILDER_NAME, type TaskAiMachine, type TaskContextSearchHit, type TaskMember } from '@shared/types/tasks';
import type { PickedMention } from './mention-text';
import { memberName } from './task-chips';

/**
 * Whether this project has the AI builder, for the task text under it: the @ list offers
 * "AI builder" and its mentions show as mentions. Off, a pasted one is plain text.
 */
export const AiBuilderMentions = createContext(false);

export function useAiBuilderMentions(override?: boolean): boolean {
  const fromTask = useContext(AiBuilderMentions);
  return override ?? fromTask;
}

export const AI_BUILDER_PICK: PickedMention = { label: TASK_AI_BUILDER_NAME, type: AI_BUILDER_MENTION_TYPE, id: AI_BUILDER_MENTION_ID };

/** SF-T-174: "@AI builder on jackalope": the comment asks the AI builder and sets the task's machine. */
export function aiBuilderMachinePick(machine: TaskAiMachine): PickedMention {
  return { label: `${TASK_AI_BUILDER_NAME} on ${machine.name}`, type: AI_BUILDER_MENTION_TYPE, id: aiBuilderMentionId(machine.id) };
}

/** SF-T-174: a machine matches what the AI builder does, or its own name, or "ai builder on j". */
function aiMachineMentionMatches(query: string, machine: TaskAiMachine): boolean {
  const q = query.trim().toLowerCase();
  const name = machine.name.toLowerCase();
  return aiBuilderMatches(q) || name.startsWith(q) || `${TASK_AI_BUILDER_NAME} on ${name}`.toLowerCase().startsWith(q);
}

/** The AI builder matches an empty query, or one a word of its name starts with ("ai", "build", "ai b"). */
export function aiBuilderMatches(query: string): boolean {
  const q = query.trim().toLowerCase();
  const name = TASK_AI_BUILDER_NAME.toLowerCase();
  return !q || name.startsWith(q) || name.split(' ').some((word) => word.startsWith(q));
}

/** `machine` (SF-T-174): the AI builder on that fleet machine. */
export type MentionSuggestion = { kind: 'user'; member: TaskMember } | { kind: 'ai'; machine?: TaskAiMachine } | { kind: 'context'; hit: TaskContextSearchHit };

/**
 * What the @ list offers for a query: up to five people, the AI builder when the project has it (then,
 * SF-T-174, the AI builder on each of `aiMachines`), then records.
 */
export function mentionSuggestions(
  query: string,
  members: TaskMember[],
  hits: TaskContextSearchHit[],
  aiBuilder: boolean,
  aiMachines: TaskAiMachine[] = []
): MentionSuggestion[] {
  const q = query.toLowerCase();
  const people = members
    .filter(
      (m) =>
        !q || memberName(m).toLowerCase().includes(q) || m.email.toLowerCase().startsWith(q) || Boolean(m.handle?.toLowerCase().startsWith(q))
    )
    .slice(0, 5)
    .map((member): MentionSuggestion => ({ kind: 'user', member }));
  const ai: MentionSuggestion[] = aiBuilder && aiBuilderMatches(q) ? [{ kind: 'ai' }] : [];
  const onMachines: MentionSuggestion[] = aiBuilder
    ? aiMachines.filter((m) => aiMachineMentionMatches(q, m)).map((machine): MentionSuggestion => ({ kind: 'ai', machine }))
    : [];
  return [...people, ...ai, ...onMachines, ...hits.map((hit): MentionSuggestion => ({ kind: 'context', hit }))];
}
